import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import { baseURL } from "../../../config/api";
import CustomFormatDatePicker from "../../../components/CustomFormatDatePicker";

export const EditFuelSurchargeRule = () => {
    const navigate = useNavigate();
    const idNumber = useLocation();

    const [ruleData, setRuleData] =
        useState<any>(null);

    const [vehicleTypeId, setVehicleTypeId] =
        useState<string>("");

    const [seasonId, setSeasonId] =
        useState<string>("");

    const [calculationType, setCalculationType] =
        useState<string>("fixed");

    const [calculationBase, setCalculationBase] =
        useState<string>("base_cost");

    const [amount, setAmount] =
        useState<string>("0");

    const [percentage, setPercentage] =
        useState<string>("0");

    const [minimumAmount, setMinimumAmount] =
        useState<string>("0");

    const [maximumAmount, setMaximumAmount] =
        useState<string>("0");

    const [priority, setPriority] =
        useState<string>("0");

    const [validFrom, setValidFrom] =
        useState<string>("");

    const [validTo, setValidTo] =
        useState<string>("");

    const [enabled, setEnabled] =
        useState<string>("Yes");

    const [description, setDescription] =
        useState<string>("");

    const [createdBy, setCreatedBy] =
        useState<string>("");

    const [pleaseWaitPopup, setPleaseWaitPopup] =
        useState<boolean>(false);

    const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);

    const [seasonOptions, setSeasonOptions] = useState([]);

    useEffect(() => {
        axios.get(`${baseURL}vehicle-types/`)
            .then((response) => {
                const vehicleTypes = response.data.data || [];

                setVehicleTypeOptions(
                    vehicleTypes.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }))
                );

            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        axios.get(`${baseURL}seasons/`)
            .then((response) => {
                const seasons = response.data.data || [];

                setSeasonOptions(
                    seasons.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }))
                );
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);


    useEffect(() => {
        setPleaseWaitPopup(true);

        axios
            .get(
                `${baseURL}fuel-surcharge-rules/${idNumber?.state}`
            )
            .then((response) => {
                const rule =
                    response?.data?.data;

                setRuleData(rule);

                setVehicleTypeId(
                    rule?.vehicle_type_id != null
                        ? String(rule.vehicle_type_id)
                        : ""
                );

                setSeasonId(
                    rule?.season_id != null
                        ? String(rule.season_id)
                        : ""
                );

                setCalculationType(
                    rule?.calculation_type ??
                    "fixed"
                );

                setCalculationBase(
                    rule?.calculation_base ??
                    "base_cost"
                );

                setAmount(
                    rule?.amount != null
                        ? String(rule.amount)
                        : "0"
                );

                setPercentage(
                    rule?.percentage != null
                        ? String(rule.percentage)
                        : "0"
                );

                setMinimumAmount(
                    rule?.minimum_amount != null
                        ? String(rule.minimum_amount)
                        : "0"
                );

                setMaximumAmount(
                    rule?.maximum_amount != null
                        ? String(rule.maximum_amount)
                        : "0"
                );

                setPriority(
                    rule?.priority != null
                        ? String(rule.priority)
                        : "0"
                );

                setValidFrom(
                    rule?.valid_from ?? ""
                );

                setValidTo(
                    rule?.valid_to ?? ""
                );

                setEnabled(
                    rule?.is_active
                        ? "Yes"
                        : "No"
                );

                setDescription(
                    rule?.description ?? ""
                );

                setCreatedBy(
                    rule?.created_by ?? ""
                );

                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error fetching fuel surcharge rule:",
                    error
                );
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);


    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        const payload = {
            ...ruleData,

            vehicle_type_id:
                Number(vehicleTypeId),

            season_id:
                Number(seasonId),

            calculation_type:
                calculationType,

            calculation_base:
                calculationBase,

            amount:
                Number(amount),

            percentage:
                Number(percentage),

            minimum_amount:
                Number(minimumAmount),

            maximum_amount:
                Number(maximumAmount),

            priority:
                Number(priority),

            valid_from:
                validFrom
                    ? new Date(
                        validFrom
                    ).toISOString()
                    : null,

            valid_to:
                validTo
                    ? new Date(
                        validTo
                    ).toISOString()
                    : null,

            is_active:
                enabled === "Yes",

            description:
                description.trim(),

            created_by:
                createdBy.trim()
        };

        await axios
            .put(
                `${baseURL}fuel-surcharge-rules/${idNumber?.state}`,
                payload,
                {
                    headers: {
                        "Content-Type":
                            "application/json"
                    },
                    withCredentials: false
                }
            )
            .then(() => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error updating fuel surcharge rule:",
                    error
                );
            });
    };

    return (
        <>
            <div className="px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto">

                <div className="w-full bg-white p-4 rounded-md">

                    <h2 className="text-lg font-semibold">
                        Edit Fuel Surcharge Rule
                    </h2>

                    <div className="mt-2 p-2">

                        <form onSubmit={handleSubmit}>

                            <div className="w-full">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Vehicle Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={vehicleTypeId}
                                            onChange={(e) => setVehicleTypeId(e.target.value)}
                                        >
                                            {vehicleTypeOptions?.map((vehicleTypes: any) => (
                                                <option key={vehicleTypes.value} value={String(vehicleTypes.value)}>
                                                    {vehicleTypes.label}
                                                </option>
                                            ))}

                                        </select>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Season{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={seasonId}
                                            onChange={(e) => setSeasonId(e.target.value)}
                                        >
                                            {seasonOptions?.map((seasons: any) => (
                                                <option key={seasons.value} value={String(seasons.value)}>
                                                    {seasons.label}
                                                </option>
                                            ))}

                                        </select>
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Calculation Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={calculationType}
                                            onChange={(e) =>
                                                setCalculationType(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            <option value="fixed">
                                                Fixed
                                            </option>

                                            <option value="percentage">
                                                Percentage
                                            </option>

                                            <option value="per_mile">
                                                Per Mile
                                            </option>
                                        </select>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Calculation Base{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={calculationBase}
                                            onChange={(e) =>
                                                setCalculationBase(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            <option value="base_cost">
                                                Base Cost
                                            </option>

                                            <option value="duration_cost">
                                                Duration Cost
                                            </option>

                                            <option value="mileage_cost">
                                                Mileage Cost
                                            </option>

                                            <option value="selected_base_cost">
                                                Selected Base Cost
                                            </option>

                                            <option value="subtotal">
                                                Subtotal
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    {(
                                        calculationType === "fixed" ||
                                        calculationType === "per_mile"
                                    ) && (
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                    {calculationType === "per_mile"
                                                        ? "Amount Per Mile"
                                                        : "Amount"}

                                                    <span className="text-red-500">
                                                        {" "}*
                                                    </span>
                                                </label>

                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="any"
                                                    className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                    value={amount}
                                                    onChange={(e) =>
                                                        setAmount(e.target.value)
                                                    }
                                                    required
                                                />
                                            </div>
                                        )}


                                    {calculationType === "percentage" && (
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                Percentage
                                                <span className="text-red-500">
                                                    {" "}*
                                                </span>
                                            </label>

                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="any"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                value={percentage}
                                                onChange={(e) =>
                                                    setPercentage(e.target.value)
                                                }
                                                required
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Priority
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={priority}
                                            onChange={(e) =>
                                                setPriority(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Minimum Amount
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={minimumAmount}
                                            onChange={(e) =>
                                                setMinimumAmount(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Maximum Amount
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={maximumAmount}
                                            onChange={(e) =>
                                                setMaximumAmount(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Valid From
                                        </label>

                                        <CustomFormatDatePicker
                                            value={validFrom}
                                            onChange={(value) => {
                                                setValidFrom(value);
                                            }}
                                            required={true}
                                        />
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Valid To
                                        </label>

                                        <CustomFormatDatePicker
                                            minDate={
                                                validFrom ||
                                                undefined
                                            }
                                            value={validTo}
                                            onChange={(value) => {
                                                setValidTo(value);
                                            }}
                                            required={true}
                                        />
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Active
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={enabled}
                                            onChange={(e) =>
                                                setEnabled(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="No">
                                                No
                                            </option>

                                            <option value="Yes">
                                                Yes
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="mb-4">

                                    <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                        Description
                                    </label>

                                    <textarea
                                        rows={4}
                                        value={description}
                                        onChange={(e) =>
                                            setDescription(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter fuel surcharge rule description"
                                        className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                    />

                                </div>


                                <div className="flex flex-col sm:flex-row gap-4 mt-6">

                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(-1)
                                        }
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-white text-primary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>
                </div>
            </div>

            <PleasewaitPopup
                showPopup={pleaseWaitPopup}
            />
        </>
    );
};